import { fromJS, List, Record } from 'immutable';
import {
  generateKeys,
  generateSchema,
  setInModel,
} from '../../../utils/helpers/model.helper';
import { Client } from '../../client-data/client.model';
import { CompanyInfo } from '../../settings-data/sub-models/company-info.model';
import { DocumentPresetSettings } from '../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { StatementAging } from './statement-aging.model';
import { StatementContent } from './statement-content.model';
import { StatementDocument } from './statement-document.model';
import { StatementLabels } from './statement-labels.model';
import { StatementSummary } from './statement-summary.model';
import { StatementLinks } from './statement-links.model';

// wrapping statement content, settings, company info and client so it's available for renderer in one immutable object
export const STATEMENT_SCHEMA = Object.freeze({
  CONTENT: StatementContent,
  SETTINGS: DocumentPresetSettings,
  COMPANY_INFO: CompanyInfo,
  CLIENT_CONTENT: Client,
  LINKS: StatementLinks,
  VIEW_ID: '',
});
const RECORD = generateSchema(STATEMENT_SCHEMA);
const KEYS: any = generateKeys(STATEMENT_SCHEMA);
export const CONTENT_KEYS: any = StatementContent.KEYS;

export class Statement extends Record(RECORD) {
  static KEYS: any = KEYS;
  static CONTENT_KEYS: any = CONTENT_KEYS;

  constructor(props?: any) {
    super(props && props.toMap ? props.toMap() : fromJS(props || {}));

    const statement = setInModel(this, [
      {
        key: KEYS.CONTENT,
        value: new StatementContent(this.content),
      },
      {
        key: KEYS.SETTINGS,
        value: new DocumentPresetSettings(this.settings),
      },
      {
        key: KEYS.COMPANY_INFO,
        value: new CompanyInfo(this.companyInfo),
      },
      {
        key: KEYS.CLIENT_CONTENT,
        value: new Client(this.clientContent),
      },
      {
        key: KEYS.LINKS,
        value: new StatementLinks(this.links),
      },
    ]);

    return statement as Statement;
  }

  get content(): StatementContent {
    return this.get(KEYS.CONTENT);
  }

  get settings(): DocumentPresetSettings {
    return this.get(KEYS.SETTINGS);
  }

  get companyInfo(): CompanyInfo {
    return this.get(KEYS.COMPANY_INFO);
  }

  get clientContent(): Client {
    return this.get(KEYS.CLIENT_CONTENT);
  }

  get statementDate(): string {
    return this.content.statementDate;
  }

  get clientId(): string {
    return this.content.clientId;
  }

  get startDate(): string {
    return this.content.startDate;
  }

  get endDate(): string {
    return this.content.endDate;
  }

  get unpaidOnly(): boolean {
    return this.content.unpaidOnly;
  }

  get amountDue(): number {
    return this.content.amountDue;
  }

  get summary(): StatementSummary {
    return this.content.summary;
  }

  get documents(): List<StatementDocument> {
    return this.content.documents;
  }

  get aging(): StatementAging {
    return this.content.aging;
  }

  get labels(): StatementLabels {
    return this.content.labels;
  }

  get links(): StatementLinks {
    return this.get(KEYS.LINKS);
  }

  get viewId(): string {
    return this.get(KEYS.VIEW_ID);
  }

  serialize(): Statement {
    return this.set(
      KEYS.CLIENT_CONTENT,
      this.clientContent.content
    ) as Statement;
  }
}
